import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, getApiHeaders, API_ERROR_MESSAGES } from '../config/api';

class AuthService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async login(credentials) {
    try {
      const response = await axios.post(`${this.baseURL}${API_ENDPOINTS.auth.login}`, credentials);
      
      if (response.data.access_token) {
        // Store tokens in localStorage
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        return response.data;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      this.handleError(error);
    }
  }

  async logout() {
    try {
      const token = this.getToken();
      if (token) {
        // Call logout endpoint for logging purposes
        await axios.post(
          `${this.baseURL}${API_ENDPOINTS.auth.logout}`,
          {},
          { headers: getApiHeaders(token) }
        );
      }
    } catch (error) {
      // Even if logout fails on server, we still want to clear local storage
      console.warn('Logout request failed:', error.message);
    } finally {
      // Always clear local storage regardless of server response
      this.clearTokens();
    }
  }

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${this.baseURL}${API_ENDPOINTS.auth.refresh}`, {
        refresh_token: refreshToken,
      });

      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        return response.data.access_token;
      }

      throw new Error('Invalid refresh response');
    } catch (error) {
      this.clearTokens();
      this.handleError(error);
    }
  }

  async getProfile() {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await axios.get(
        `${this.baseURL}${API_ENDPOINTS.auth.profile}`,
        { headers: getApiHeaders(token) }
      );

      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  getToken() {
    return localStorage.getItem('access_token');
  }

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  isSuperAdmin() {
    const user = this.getUser();
    return user?.isSuperAdmin === true;
  }

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  handleError(error) {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          this.clearTokens();
          throw new Error(API_ERROR_MESSAGES.UNAUTHORIZED);
        case 403:
          throw new Error(API_ERROR_MESSAGES.FORBIDDEN);
        case 404:
          throw new Error(API_ERROR_MESSAGES.NOT_FOUND);
        case 422:
          throw new Error(data.message || API_ERROR_MESSAGES.VALIDATION_ERROR);
        case 500:
          throw new Error(API_ERROR_MESSAGES.SERVER_ERROR);
        default:
          throw new Error(data.message || `HTTP ${status} error`);
      }
    } else if (error.request) {
      throw new Error(API_ERROR_MESSAGES.NETWORK_ERROR);
    } else {
      throw new Error(error.message || API_ERROR_MESSAGES.SERVER_ERROR);
    }
  }
}

export default new AuthService();
